import React, { useState, useEffect, useRef } from 'react';
import { CalendarIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { getAvailableTimeSlots, formatDate, isDateClosed, isDateInFuture, isDateWithinAdvanceLimit } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return formatDate(a) === formatDate(b);
};

const SERVICE_LABELS = {
  lunch: 'Mittagstisch',
  dinner: 'Abendservice'
};

const groupTimeSlots = (slots) => {
  const groups = [];
  const lunch = slots.filter((slot) => slot.service === 'lunch');
  const dinner = slots.filter((slot) => slot.service !== 'lunch');

  if (lunch.length) {
    groups.push({ key: 'lunch', title: SERVICE_LABELS.lunch, slots: lunch });
  }
  if (dinner.length) {
    groups.push({ key: 'dinner', title: SERVICE_LABELS.dinner, slots: dinner });
  }
  return groups;
};

const getAvailableRange = (slots) => {
  const available = slots.filter((slot) => slot.available);
  if (!available.length) {
    return null;
  }
  return {
    count: available.length,
    first: available[0].time,
    last: available[available.length - 1].time
  };
};

const TimeSlotButton = ({ slot, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(slot.time)}
    disabled={!slot.available}
    className={`kdw-time-slot ${slot.available ? 'kdw-time-slot--available' : 'kdw-time-slot--unavailable'}`}
    aria-label={
      slot.available
        ? `Uhrzeit ${slot.time} wählen`
        : `Uhrzeit ${slot.time} nicht verfügbar`
    }
  >
    {slot.time}
  </button>
);

const TimeSlotGroup = ({ title, slots, onSelect }) => {
  const range = getAvailableRange(slots);

  return (
    <section className="kdw-time-group" aria-label={title}>
      <header className="kdw-time-group__header">
        <h4 className="kdw-time-group__title">{title}</h4>
        <p className="kdw-time-group__meta">
          {range ? `${range.first}–${range.last}` : 'Keine freien Zeiten'}
        </p>
      </header>
      <div className="kdw-time-grid">
        {slots.map((slot) => (
          <TimeSlotButton key={slot.time} slot={slot} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
};

const BookingCalendar = ({ settings, onDateTimeSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState(2);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [error, setError] = useState(null);
  const timeSectionRef = useRef(null);

  // Verfügbare Zeitslots laden wenn Datum ausgewählt
  useEffect(() => {
    const loadTimeSlots = async () => {
      if (!selectedDate) {
        setAvailableTimeSlots([]);
        return;
      }

      setLoadingTimeSlots(true);
      setError(null);

      try {
        const timeSlots = await getAvailableTimeSlots(
          formatDate(selectedDate), 
          selectedGuests
        );
        setAvailableTimeSlots(timeSlots);
      } catch (err) {
        setError('Fehler beim Laden der verfügbaren Zeiten');
        console.error('Error loading time slots:', err);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    loadTimeSlots();
  }, [selectedDate, selectedGuests]);

  useEffect(() => {
    if (!selectedDate || loadingTimeSlots || availableTimeSlots.length === 0) {
      return;
    }

    const section = timeSectionRef.current;
    if (!section || typeof window === 'undefined') {
      return;
    }

    const isStackedLayout = window.matchMedia('(max-width: 1023px)').matches;
    if (!isStackedLayout) {
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    section.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'nearest'
    });
  }, [selectedDate, loadingTimeSlots, availableTimeSlots.length]);

  const selectTimeSlot = (time) => {
    if (selectedDate && time) {
      onDateTimeSelect(formatDate(selectedDate), time, selectedGuests);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + direction);
      return startOfDay(next);
    });
  };

  const isDateSelectable = (date) => {
    return (
      isDateInFuture(date) &&
      isDateWithinAdvanceLimit(date, settings.max_advance_days) &&
      !isDateClosed(date, settings)
    );
  };

  const selectDate = (date) => {
    if (!isDateSelectable(date)) return;
    setSelectedDate(date);
  };

  const timeGroups = groupTimeSlots(availableTimeSlots);
  const timeRange = getAvailableRange(availableTimeSlots);
  const today = startOfDay(new Date());

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="kdw-cal-empty" aria-hidden="true" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const selectable = isDateSelectable(date);
      const selected = isSameDay(selectedDate, date);
      const closed = isDateClosed(date, settings);
      const isToday = isSameDay(today, date);

      let status = 'unavailable';
      if (selected) status = 'selected';
      else if (selectable) status = 'available';
      else if (closed) status = 'closed';

      const label = date.toLocaleDateString('de-DE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });

      cells.push(
        <button
          key={day}
          type="button"
          onClick={() => selectDate(date)}
          disabled={!selectable}
          aria-pressed={selected}
          aria-current={isToday ? 'date' : undefined}
          aria-label={
            selectable
              ? `${label} wählen`
              : closed
                ? `${label}, geschlossen`
                : `${label}, nicht verfügbar`
          }
          className={`kdw-cal-day kdw-cal-day--${status}${isToday ? ' kdw-cal-day--today' : ''}`}
        >
          {day}
        </button>
      );
    }

    return cells;
  };

  return (
    <div className="bg-white rounded-lg border-2 border-gray-300 p-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
        
        {/* Datum */}
        <div className="kdw-date-section">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <CalendarIcon className="w-6 h-6 mr-2 text-restaurant-primary" />
            Datum wählen
          </h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Anzahl Personen
            </label>
            <select
              value={selectedGuests}
              onChange={(e) => setSelectedGuests(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-restaurant-primary focus:border-transparent"
            >
              {Array.from({length: settings.max_guests_per_booking}, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'Person' : 'Personen'}
                </option>
              ))}
            </select>
          </div>

          <p className="kdw-date-summary">
            {selectedDate
              ? `Ausgewählt: ${selectedDate.toLocaleDateString('de-DE', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}`
              : 'Bitte wählen Sie einen verfügbaren Tag'}
          </p>

          <div className="kdw-cal">
            <div className="kdw-cal-nav">
              <button
                type="button"
                onClick={() => navigateMonth(-1)}
                className="kdw-cal-nav__btn"
                aria-label="Vorheriger Monat"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <h3 className="kdw-cal-nav__title">
                {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button
                type="button"
                onClick={() => navigateMonth(1)}
                className="kdw-cal-nav__btn"
                aria-label="Nächster Monat"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="kdw-cal-weekdays">
              {WEEKDAYS.map((day) => (
                <div key={day} className="kdw-cal-weekday">{day}</div>
              ))}
            </div>

            <div className="kdw-cal-grid">
              {renderCalendarDays()}
            </div>
          </div>

          <ul className="kdw-cal-legend">
            <li><span className="kdw-cal-legend__swatch kdw-cal-legend__swatch--selected" />Ausgewählt</li>
            <li><span className="kdw-cal-legend__swatch kdw-cal-legend__swatch--available" />Verfügbar</li>
            <li><span className="kdw-cal-legend__swatch kdw-cal-legend__swatch--closed" />Geschlossen</li>
            <li><span className="kdw-cal-legend__swatch kdw-cal-legend__swatch--unavailable" />Nicht verfügbar</li>
          </ul>
        </div>

        {/* Zeitauswahl */}
        <div ref={timeSectionRef} className="kdw-time-section">
          <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <ClockIcon className="w-6 h-6 mr-2 text-restaurant-primary" />
            Uhrzeit wählen
          </h3>

          {!selectedDate ? (
            <div className="text-center text-gray-500 py-12">
              <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Bitte wählen Sie zuerst ein Datum</p>
            </div>
          ) : loadingTimeSlots ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-12">
              <p>{error}</p>
              <button 
                onClick={() => setSelectedDate(selectedDate)}
                className="mt-2 text-sm text-restaurant-primary hover:underline"
              >
                Erneut versuchen
              </button>
            </div>
          ) : availableTimeSlots.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <p>Keine verfügbaren Zeiten für dieses Datum</p>
              <p className="text-sm mt-2">
                Bitte wählen Sie ein anderes Datum oder kontaktieren Sie uns telefonisch
              </p>
            </div>
          ) : (
            <div>
              <p className="kdw-time-summary">
                Verfügbare Zeiten für {selectedDate.toLocaleDateString('de-DE', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
                {timeRange && (
                  <>
                    {' · '}
                    <span className="kdw-time-summary__range">
                      {timeRange.count === 1
                        ? `1 Uhrzeit um ${timeRange.first}`
                        : `${timeRange.count} Uhrzeiten von ${timeRange.first} bis ${timeRange.last}`}
                    </span>
                  </>
                )}
              </p>

              <div className="kdw-time-groups">
                {timeGroups.map((group) => (
                  <TimeSlotGroup
                    key={group.key}
                    title={group.title}
                    slots={group.slots}
                    onSelect={selectTimeSlot}
                  />
                ))}
              </div>
              
              <div className="mt-6 p-5 bg-restaurant-background rounded-xl border-2 border-restaurant-light">
                <p className="text-sm text-restaurant-muted font-medium">
                  <strong className="text-restaurant-text">Hinweis:</strong> Nach der Zeitauswahl können Sie Ihre Kontaktdaten eingeben 
                  und die Reservierung abschließen.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar; 